import React, { useState, useRef } from 'react';
import { useSiteData } from '../context/SiteDataContext';
import { Save, RotateCcw, Home, Settings, Image, Euro, Upload, Trash2, Plus, LogOut, FileText, ChevronDown, ChevronUp, Scale } from 'lucide-react';
import { Link } from 'react-router-dom';

const ADMIN_PASSWORD = 'ecotint2024';

const LOGO_SIZES = [
  { value: 1, label: 'Petit', classes: 'h-16 sm:h-20 md:h-24 lg:h-32' },
  { value: 2, label: 'Moyen', classes: 'h-24 sm:h-32 md:h-48 lg:h-64' },
  { value: 3, label: 'Grand', classes: 'h-32 sm:h-48 md:h-72 lg:h-96' },
  { value: 4, label: 'Très grand', classes: 'h-40 sm:h-56 md:h-80 lg:h-[28rem]' },
];

const ImageUploader = ({ label, currentImage, onImageChange, uploadImage }) => {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const url = await uploadImage(file);
      onImageChange(url);
    } catch (err) {
      alert('Erreur: ' + err.message);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div>
      <label className="block text-white/60 text-sm mb-2">{label}</label>
      <div className="flex items-center gap-3 mb-2">
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
          className="flex items-center gap-2 bg-white/10 hover:bg-white/20 text-white px-4 py-2.5 text-sm transition-colors rounded disabled:opacity-50"
          data-testid={`upload-btn-${label.toLowerCase().replace(/\s/g, '-')}`}
        >
          <Upload size={16} />
          {uploading ? 'Upload en cours...' : 'Choisir un fichier'}
        </button>
        <input ref={fileRef} type="file" accept="image/*" onChange={handleUpload} className="hidden" />
      </div>
      {currentImage && (
        <div className="mt-2 p-3 bg-white/5 rounded-lg">
          <img
            src={currentImage}
            alt="Aperçu"
            className="h-20 max-w-full object-contain rounded"
            onError={(e) => { e.target.style.opacity = '0.3'; }}
          />
        </div>
      )}
    </div>
  );
};

const AdminPage = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    return sessionStorage.getItem('ecotint_admin_auth') === 'true';
  });
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('company');
  const [saveMessage, setSaveMessage] = useState('');
  const [saving, setSaving] = useState(false);

  const {
    companyInfo, setCompanyInfo,
    logoSize, setLogoSize,
    reprogrammingServices, setReprogrammingServices,
    tintingPrices, setTintingPrices,
    tintingBenefits, setTintingBenefits,
    services, setServices,
    heroImage, setHeroImage,
    galleryImages, setGalleryImages,
    pageContent, setPageContent,
    cgv, setCgv,
    saveAll, uploadImage, fetchData
  } = useSiteData();

  const handleLogin = (e) => {
    e.preventDefault();
    if (password === ADMIN_PASSWORD) {
      setIsAuthenticated(true);
      sessionStorage.setItem('ecotint_admin_auth', 'true');
      setError('');
    } else {
      setError('Mot de passe incorrect');
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    sessionStorage.removeItem('ecotint_admin_auth');
  };

  const handleSave = async (overrides = {}) => {
    setSaving(true);
    try {
      await saveAll(overrides);
      setSaveMessage('Modifications enregistrées');
      setTimeout(() => setSaveMessage(''), 3000);
    } catch (err) {
      setSaveMessage('Erreur lors de la sauvegarde');
      setTimeout(() => setSaveMessage(''), 3000);
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (window.confirm('Voulez-vous vraiment réinitialiser toutes les données ?')) {
      await fetchData();
      setSaveMessage('Données rechargées');
      setTimeout(() => setSaveMessage(''), 3000);
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-neutral-950 flex items-center justify-center px-4">
        <div className="max-w-md w-full">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-white">Administration</h1>
            <p className="text-white/60 mt-2">Ecotint & Reprog</p>
          </div>
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="block text-white/60 text-sm mb-2">Mot de passe</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="admin-password-input"
                className="w-full bg-black border border-white/20 px-4 py-3 text-white focus:border-red-500 focus:outline-none rounded"
                placeholder="Entrez le mot de passe"
              />
            </div>
            {error && <p className="text-red-500 text-sm">{error}</p>}
            <button
              type="submit"
              data-testid="admin-login-btn"
              className="w-full bg-red-600 hover:bg-red-700 text-white py-3 font-medium transition-colors rounded"
            >
              Connexion
            </button>
          </form>
          <div className="mt-6 text-center">
            <Link to="/" className="text-white/60 hover:text-white text-sm">Retour au site</Link>
          </div>
        </div>
      </div>
    );
  }

  const tabs = [
    { id: 'company', label: 'Entreprise', icon: Settings },
    { id: 'content', label: 'Contenu', icon: FileText },
    { id: 'reprog', label: 'Reprogrammation', icon: Euro },
    { id: 'tinting', label: 'Films Solaires', icon: Euro },
    { id: 'images', label: 'Images', icon: Image },
    { id: 'services', label: 'Services', icon: Home },
    { id: 'cgv', label: 'CGV', icon: Scale },
  ];

  return (
    <div className="min-h-screen bg-neutral-950" data-testid="admin-panel">
      <header className="bg-black border-b border-white/10 px-4 py-4">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <h1 className="text-xl font-bold text-white">Administration</h1>
          <div className="flex items-center gap-4">
            <Link to="/" className="text-white/60 hover:text-white text-sm flex items-center gap-2">
              <Home size={16} /> Voir le site
            </Link>
            <button onClick={handleLogout} className="text-white/60 hover:text-white text-sm flex items-center gap-2">
              <LogOut size={16} /> Déconnexion
            </button>
          </div>
        </div>
      </header>

      {saveMessage && (
        <div className="fixed top-4 right-4 bg-green-600 text-white px-4 py-2 rounded z-50" data-testid="save-message">
          {saveMessage}
        </div>
      )}

      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="flex flex-wrap gap-2 mb-8">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              data-testid={`tab-${tab.id}`}
              className={`flex items-center gap-2 px-4 py-2 text-sm font-medium transition-colors rounded ${
                activeTab === tab.id ? 'bg-red-600 text-white' : 'bg-white/5 text-white/60 hover:text-white'
              }`}
            >
              <tab.icon size={16} />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Company Info Tab */}
        {activeTab === 'company' && (
          <div className="space-y-6">
            <h2 className="text-xl font-bold text-white">Informations de l'entreprise</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[
                { key: 'name', label: "Nom de l'entreprise" },
                { key: 'phone', label: 'Téléphone' },
                { key: 'address', label: 'Adresse' },
                { key: 'city', label: 'Ville / Code postal' },
                { key: 'email', label: 'Email' },
              ].map(({ key, label }) => (
                <div key={key}>
                  <label className="block text-white/60 text-sm mb-2">{label}</label>
                  <input
                    type="text"
                    value={companyInfo[key] || ''}
                    onChange={(e) => setCompanyInfo({ ...companyInfo, [key]: e.target.value })}
                    data-testid={`input-${key}`}
                    className="w-full bg-black border border-white/20 px-4 py-3 text-white focus:border-red-500 focus:outline-none rounded"
                  />
                </div>
              ))}
            </div>

            {/* Logo Upload */}
            <div className="md:col-span-2">
              <ImageUploader
                label="Logo de l'entreprise"
                currentImage={companyInfo.logo}
                onImageChange={(url) => setCompanyInfo({ ...companyInfo, logo: url })}
                uploadImage={uploadImage}
              />
            </div>

            {/* Logo Size Slider */}
            <div>
              <label className="block text-white/60 text-sm mb-3">
                Taille du logo sur la page d'accueil : <span className="text-red-500 font-medium">{LOGO_SIZES.find(s => s.value === logoSize)?.label}</span>
              </label>
              <input
                type="range"
                min={1}
                max={4}
                step={1}
                value={logoSize}
                onChange={(e) => setLogoSize(parseInt(e.target.value))}
                data-testid="logo-size-slider"
                className="w-full max-w-md accent-red-600"
              />
              <div className="flex justify-between max-w-md text-xs text-white/40 mt-1">
                {LOGO_SIZES.map(s => <span key={s.value}>{s.label}</span>)}
              </div>
              {/* Preview */}
              {companyInfo.logo && (
                <div className="mt-4 p-4 bg-white/5 rounded-lg">
                  <p className="text-white/40 text-xs mb-2">Aperçu taille :</p>
                  <img
                    src={companyInfo.logo}
                    alt="Logo preview"
                    className={`${LOGO_SIZES.find(s => s.value === logoSize)?.classes || 'h-32'} w-auto object-contain`}
                  />
                </div>
              )}
            </div>

            <button
              onClick={() => handleSave()}
              disabled={saving}
              data-testid="save-company-btn"
              className="flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white px-6 py-3 font-medium transition-colors rounded disabled:opacity-50"
            >
              <Save size={18} /> {saving ? 'Enregistrement...' : 'Enregistrer'}
            </button>
          </div>
        )}

        {/* Content Tab */}
        {activeTab === 'content' && (
          <ContentTab
            pageContent={pageContent}
            setPageContent={setPageContent}
            uploadImage={uploadImage}
            handleSave={handleSave}
            saving={saving}
          />
        )}

        {/* Reprogrammation Tab */}
        {activeTab === 'reprog' && (
          <div className="space-y-6">
            <h2 className="text-xl font-bold text-white">Tarifs Reprogrammation</h2>
            <div className="space-y-4">
              {reprogrammingServices.map((service, index) => (
                <div key={service.id} className="border border-white/10 p-4 rounded">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-white/60 text-sm mb-2">Nom court</label>
                      <input
                        type="text"
                        value={service.shortName}
                        onChange={(e) => {
                          const updated = [...reprogrammingServices];
                          updated[index] = { ...updated[index], shortName: e.target.value };
                          setReprogrammingServices(updated);
                        }}
                        className="w-full bg-black border border-white/20 px-4 py-2 text-white focus:border-red-500 focus:outline-none rounded"
                      />
                    </div>
                    <div>
                      <label className="block text-white/60 text-sm mb-2">Prix (€)</label>
                      <input
                        type="number"
                        value={service.price}
                        onChange={(e) => {
                          const updated = [...reprogrammingServices];
                          updated[index] = { ...updated[index], price: parseInt(e.target.value) || 0 };
                          setReprogrammingServices(updated);
                        }}
                        className="w-full bg-black border border-white/20 px-4 py-2 text-white focus:border-red-500 focus:outline-none rounded"
                      />
                    </div>
                    <div className="flex items-end">
                      <button
                        onClick={() => setReprogrammingServices(reprogrammingServices.filter((_, i) => i !== index))}
                        className="text-red-500 hover:text-red-400 p-2"
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </div>
                  <div className="mt-4">
                    <label className="block text-white/60 text-sm mb-2">Description</label>
                    <textarea
                      value={service.description}
                      onChange={(e) => {
                        const updated = [...reprogrammingServices];
                        updated[index] = { ...updated[index], description: e.target.value };
                        setReprogrammingServices(updated);
                      }}
                      rows={2}
                      className="w-full bg-black border border-white/20 px-4 py-2 text-white focus:border-red-500 focus:outline-none resize-none rounded"
                    />
                  </div>
                </div>
              ))}
            </div>
            <button
              onClick={() => setReprogrammingServices([...reprogrammingServices, {
                id: Date.now(), name: 'Nouvelle prestation', shortName: 'NOUVEAU', description: '', price: 0, features: []
              }])}
              className="flex items-center gap-2 bg-white/10 hover:bg-white/20 text-white px-4 py-2 text-sm transition-colors rounded"
            >
              <Plus size={16} /> Ajouter une prestation
            </button>
            <button
              onClick={() => handleSave()}
              disabled={saving}
              data-testid="save-reprog-btn"
              className="flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white px-6 py-3 font-medium transition-colors rounded disabled:opacity-50"
            >
              <Save size={18} /> {saving ? 'Enregistrement...' : 'Enregistrer'}
            </button>
          </div>
        )}

        {/* Tinting Tab */}
        {activeTab === 'tinting' && (
          <div className="space-y-6">
            <h2 className="text-xl font-bold text-white">Tarifs Films Solaires</h2>
            <div className="space-y-4">
              {tintingPrices.map((item, index) => (
                <div key={item.id} className="border border-white/10 p-4 rounded">
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div>
                      <label className="block text-white/60 text-sm mb-2">Catégorie</label>
                      <input
                        type="text"
                        value={item.category}
                        onChange={(e) => {
                          const updated = [...tintingPrices];
                          updated[index] = { ...updated[index], category: e.target.value };
                          setTintingPrices(updated);
                        }}
                        className="w-full bg-black border border-white/20 px-4 py-2 text-white focus:border-red-500 focus:outline-none rounded"
                      />
                    </div>
                    <div>
                      <label className="block text-white/60 text-sm mb-2">Description</label>
                      <input
                        type="text"
                        value={item.description}
                        onChange={(e) => {
                          const updated = [...tintingPrices];
                          updated[index] = { ...updated[index], description: e.target.value };
                          setTintingPrices(updated);
                        }}
                        className="w-full bg-black border border-white/20 px-4 py-2 text-white focus:border-red-500 focus:outline-none rounded"
                      />
                    </div>
                    <div>
                      <label className="block text-white/60 text-sm mb-2">Prix 3/4 arrière (€)</label>
                      <input
                        type="number"
                        value={item.priceRear}
                        onChange={(e) => {
                          const updated = [...tintingPrices];
                          updated[index] = { ...updated[index], priceRear: parseInt(e.target.value) || 0 };
                          setTintingPrices(updated);
                        }}
                        className="w-full bg-black border border-white/20 px-4 py-2 text-white focus:border-red-500 focus:outline-none rounded"
                      />
                    </div>
                    <div>
                      <label className="block text-white/60 text-sm mb-2">Prix complet (€)</label>
                      <input
                        type="number"
                        value={item.priceComplete}
                        onChange={(e) => {
                          const updated = [...tintingPrices];
                          updated[index] = { ...updated[index], priceComplete: parseInt(e.target.value) || 0 };
                          setTintingPrices(updated);
                        }}
                        className="w-full bg-black border border-white/20 px-4 py-2 text-white focus:border-red-500 focus:outline-none rounded"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <button
              onClick={() => handleSave()}
              disabled={saving}
              data-testid="save-tinting-btn"
              className="flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white px-6 py-3 font-medium transition-colors rounded disabled:opacity-50"
            >
              <Save size={18} /> {saving ? 'Enregistrement...' : 'Enregistrer'}
            </button>
          </div>
        )}

        {/* Images Tab */}
        {activeTab === 'images' && (
          <div className="space-y-8">
            <h2 className="text-xl font-bold text-white">Images du site</h2>

            {/* Hero Image */}
            <ImageUploader
              label="Image Hero (page d'accueil)"
              currentImage={heroImage}
              onImageChange={(url) => setHeroImage(url)}
              uploadImage={uploadImage}
            />

            {/* Gallery */}
            <div>
              <label className="block text-white/60 text-sm mb-3">Galerie (Films Solaires)</label>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                {galleryImages.map((img, index) => (
                  <div key={index} className="bg-white/5 rounded-lg p-3">
                    <GalleryUploader
                      index={index}
                      currentImage={img}
                      uploadImage={uploadImage}
                      onImageChange={(url) => {
                        const updated = [...galleryImages];
                        updated[index] = url;
                        setGalleryImages(updated);
                      }}
                      onRemove={() => setGalleryImages(galleryImages.filter((_, i) => i !== index))}
                    />
                  </div>
                ))}
              </div>
              <button
                onClick={() => setGalleryImages([...galleryImages, ''])}
                className="flex items-center gap-2 bg-white/10 hover:bg-white/20 text-white px-4 py-2 text-sm mt-4 transition-colors rounded"
              >
                <Plus size={16} /> Ajouter une image
              </button>
            </div>

            <button
              onClick={() => handleSave()}
              disabled={saving}
              data-testid="save-images-btn"
              className="flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white px-6 py-3 font-medium transition-colors rounded disabled:opacity-50"
            >
              <Save size={18} /> {saving ? 'Enregistrement...' : 'Enregistrer'}
            </button>
          </div>
        )}

        {/* Services Tab */}
        {activeTab === 'services' && (
          <div className="space-y-6">
            <h2 className="text-xl font-bold text-white">Services (page d'accueil)</h2>
            <div className="space-y-4">
              {services.map((service, index) => (
                <div key={service.id} className="border border-white/10 p-4 rounded">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-white/60 text-sm mb-2">Titre</label>
                      <input
                        type="text"
                        value={service.title}
                        onChange={(e) => {
                          const updated = [...services];
                          updated[index] = { ...updated[index], title: e.target.value };
                          setServices(updated);
                        }}
                        className="w-full bg-black border border-white/20 px-4 py-2 text-white focus:border-red-500 focus:outline-none rounded"
                      />
                    </div>
                    <div>
                      <label className="block text-white/60 text-sm mb-2">Lien</label>
                      <input
                        type="text"
                        value={service.link}
                        onChange={(e) => {
                          const updated = [...services];
                          updated[index] = { ...updated[index], link: e.target.value };
                          setServices(updated);
                        }}
                        className="w-full bg-black border border-white/20 px-4 py-2 text-white focus:border-red-500 focus:outline-none rounded"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-white/60 text-sm mb-2">Description</label>
                      <textarea
                        value={service.description}
                        onChange={(e) => {
                          const updated = [...services];
                          updated[index] = { ...updated[index], description: e.target.value };
                          setServices(updated);
                        }}
                        rows={2}
                        className="w-full bg-black border border-white/20 px-4 py-2 text-white focus:border-red-500 focus:outline-none resize-none rounded"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <button
              onClick={() => handleSave()}
              disabled={saving}
              data-testid="save-services-btn"
              className="flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white px-6 py-3 font-medium transition-colors rounded disabled:opacity-50"
            >
              <Save size={18} /> {saving ? 'Enregistrement...' : 'Enregistrer'}
            </button>
          </div>
        )}

        {/* CGV Tab */}
        {activeTab === 'cgv' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-bold text-white">Conditions Générales de Vente</h2>
              <Link to="/cgv" target="_blank" className="text-white/50 hover:text-white text-sm underline">Voir la page</Link>
            </div>
            <p className="text-white/40 text-sm">
              Modifiez le texte ci-dessous. Les retours à la ligne sont conservés. Les lignes commençant par "Article" seront affichées en gras, celles par "- " en liste à puces.
            </p>
            <textarea
              value={cgv}
              onChange={(e) => setCgv(e.target.value)}
              data-testid="cgv-editor"
              rows={25}
              className="w-full bg-black border border-white/20 px-4 py-3 text-white text-sm leading-relaxed focus:border-red-500 focus:outline-none resize-y rounded font-mono"
              placeholder="Saisissez vos Conditions Générales de Vente..."
            />
            <button
              onClick={() => handleSave()}
              disabled={saving}
              data-testid="save-cgv-btn"
              className="flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white px-6 py-3 font-medium transition-colors rounded disabled:opacity-50"
            >
              <Save size={18} /> {saving ? 'Enregistrement...' : 'Enregistrer'}
            </button>
          </div>
        )}

        {/* Reset */}
        <div className="mt-12 pt-8 border-t border-white/10">
          <button
            onClick={handleReset}
            className="flex items-center gap-2 bg-white/10 hover:bg-white/20 text-white px-4 py-2 text-sm transition-colors rounded"
          >
            <RotateCcw size={16} /> Recharger les données
          </button>
        </div>
      </div>
    </div>
  );
};

const GalleryUploader = ({ index, currentImage, uploadImage, onImageChange, onRemove }) => {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const url = await uploadImage(file);
      onImageChange(url);
    } catch (err) {
      alert('Erreur: ' + err.message);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
          className="flex items-center gap-1.5 bg-white/10 hover:bg-white/20 text-white px-3 py-1.5 text-xs transition-colors rounded disabled:opacity-50"
        >
          <Upload size={12} />
          {uploading ? 'Upload...' : 'Changer'}
        </button>
        <button onClick={onRemove} className="text-red-500 hover:text-red-400 p-1">
          <Trash2 size={14} />
        </button>
        <input ref={fileRef} type="file" accept="image/*" onChange={handleUpload} className="hidden" />
      </div>
      {currentImage && (
        <img src={currentImage} alt={`Galerie ${index + 1}`} className="h-24 w-full object-cover rounded" />
      )}
    </div>
  );
};

export default AdminPage;

// --- Content Tab Component ---

const SECTIONS_CONFIG = [
  { key: 'heroTagline', label: 'Accroche page d\'accueil', type: 'tagline' },
  { key: 'optimisation', label: 'Section Optimisation (Accueil)', type: 'section' },
  { key: 'films', label: 'Section Films Solaires (Accueil)', type: 'section' },
  { key: 'depollution', label: 'Section Dépollution (Accueil)', type: 'section' },
  { key: 'e85', label: 'Section E85 (Accueil)', type: 'e85' },
  { key: 'cta', label: 'Section CTA (Accueil)', type: 'cta' },
  { key: 'reprogHero', label: 'Hero page Reprogrammation', type: 'hero' },
  { key: 'filmsHero', label: 'Hero page Films Solaires', type: 'hero' },
  { key: 'contactHero', label: 'Hero page Contact', type: 'heroNoImage' },
];

const ContentTab = ({ pageContent, setPageContent, uploadImage, handleSave, saving }) => {
  const [openSection, setOpenSection] = useState(null);

  const updateField = (sectionKey, field, value) => {
    if (sectionKey === 'heroTagline') {
      setPageContent({ ...pageContent, heroTagline: value });
    } else {
      setPageContent({
        ...pageContent,
        [sectionKey]: { ...(pageContent[sectionKey] || {}), [field]: value }
      });
    }
  };

  const updateFeature = (sectionKey, index, value) => {
    const features = [...((pageContent[sectionKey] || {}).features || [])];
    features[index] = value;
    updateField(sectionKey, 'features', features);
  };

  const addFeature = (sectionKey) => {
    const features = [...((pageContent[sectionKey] || {}).features || []), ''];
    updateField(sectionKey, 'features', features);
  };

  const removeFeature = (sectionKey, index) => {
    const features = ((pageContent[sectionKey] || {}).features || []).filter((_, i) => i !== index);
    updateField(sectionKey, 'features', features);
  };

  const updateStat = (index, field, value) => {
    const stats = [...((pageContent.e85 || {}).stats || [])];
    stats[index] = { ...stats[index], [field]: value };
    updateField('e85', 'stats', stats);
  };

  const SectionBgUpload = ({ sectionKey }) => {
    const fileRef = useRef(null);
    const [uploading, setUploading] = useState(false);
    const current = (pageContent[sectionKey] || {}).bgImage || '';

    const handleUpload = async (e) => {
      const file = e.target.files?.[0];
      if (!file) return;
      setUploading(true);
      try {
        const url = await uploadImage(file);
        updateField(sectionKey, 'bgImage', url);
      } catch (err) { alert('Erreur: ' + err.message); }
      finally { setUploading(false); }
    };

    return (
      <div className="mb-4">
        <label className="block text-white/60 text-xs mb-1.5">Image de fond</label>
        <div className="flex items-center gap-3">
          <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading}
            className="flex items-center gap-1.5 bg-white/10 hover:bg-white/20 text-white px-3 py-1.5 text-xs transition-colors rounded disabled:opacity-50">
            <Upload size={12} /> {uploading ? 'Upload...' : 'Changer l\'image'}
          </button>
          <input ref={fileRef} type="file" accept="image/*" onChange={handleUpload} className="hidden" />
        </div>
        {current && <img src={current} alt="bg" className="h-16 w-32 object-cover rounded mt-2" />}
      </div>
    );
  };

  const TextField = ({ label, value, onChange, multiline }) => (
    <div className="mb-3">
      <label className="block text-white/50 text-xs mb-1">{label}</label>
      {multiline ? (
        <textarea value={value || ''} onChange={(e) => onChange(e.target.value)} rows={2}
          className="w-full bg-black border border-white/15 px-3 py-2 text-white text-sm focus:border-red-500 focus:outline-none resize-none rounded" />
      ) : (
        <input type="text" value={value || ''} onChange={(e) => onChange(e.target.value)}
          className="w-full bg-black border border-white/15 px-3 py-2 text-white text-sm focus:border-red-500 focus:outline-none rounded" />
      )}
    </div>
  );

  return (
    <div className="space-y-3" data-testid="content-tab">
      <h2 className="text-xl font-bold text-white mb-4">Contenu des pages</h2>

      {SECTIONS_CONFIG.map(({ key, label, type }) => {
        const isOpen = openSection === key;
        const data = key === 'heroTagline' ? pageContent?.heroTagline : (pageContent?.[key] || {});

        return (
          <div key={key} className="border border-white/10 rounded-lg overflow-hidden">
            <button
              onClick={() => setOpenSection(isOpen ? null : key)}
              className="w-full flex items-center justify-between px-4 py-3 bg-white/5 hover:bg-white/10 transition-colors text-left"
              data-testid={`content-section-${key}`}
            >
              <span className="text-white font-medium text-sm">{label}</span>
              {isOpen ? <ChevronUp size={16} className="text-white/40" /> : <ChevronDown size={16} className="text-white/40" />}
            </button>

            {isOpen && (
              <div className="p-4 bg-neutral-900/50">
                {type === 'tagline' && (
                  <TextField label="Texte d'accroche sous le logo" value={data} onChange={(v) => updateField('heroTagline', null, v)} />
                )}

                {type === 'section' && (
                  <>
                    <SectionBgUpload sectionKey={key} />
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <TextField label="Catégorie" value={data.category} onChange={(v) => updateField(key, 'category', v)} />
                      <div className="flex gap-2">
                        <div className="flex-1"><TextField label="Titre" value={data.title} onChange={(v) => updateField(key, 'title', v)} /></div>
                        <div className="flex-1"><TextField label="Titre (accent rouge)" value={data.titleAccent} onChange={(v) => updateField(key, 'titleAccent', v)} /></div>
                      </div>
                    </div>
                    <TextField label="Description 1" value={data.desc1} onChange={(v) => updateField(key, 'desc1', v)} multiline />
                    <TextField label="Description 2" value={data.desc2} onChange={(v) => updateField(key, 'desc2', v)} multiline />
                    <div className="mb-3">
                      <label className="block text-white/50 text-xs mb-1">Points clés</label>
                      {(data.features || []).map((f, i) => (
                        <div key={i} className="flex items-center gap-2 mb-1.5">
                          <input type="text" value={f} onChange={(e) => updateFeature(key, i, e.target.value)}
                            className="flex-1 bg-black border border-white/15 px-3 py-1.5 text-white text-sm focus:border-red-500 focus:outline-none rounded" />
                          <button onClick={() => removeFeature(key, i)} className="text-red-500 hover:text-red-400 p-1"><Trash2 size={14} /></button>
                        </div>
                      ))}
                      <button onClick={() => addFeature(key)} className="flex items-center gap-1 text-white/50 hover:text-white text-xs mt-1"><Plus size={12} /> Ajouter</button>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <TextField label="Texte du bouton" value={data.ctaText} onChange={(v) => updateField(key, 'ctaText', v)} />
                      <TextField label="Lien du bouton" value={data.ctaLink} onChange={(v) => updateField(key, 'ctaLink', v)} />
                    </div>
                  </>
                )}

                {type === 'e85' && (
                  <>
                    <SectionBgUpload sectionKey={key} />
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <TextField label="Catégorie" value={data.category} onChange={(v) => updateField(key, 'category', v)} />
                      <div className="flex gap-2">
                        <div className="flex-1"><TextField label="Titre" value={data.title} onChange={(v) => updateField(key, 'title', v)} /></div>
                        <div className="flex-1"><TextField label="Titre (accent vert)" value={data.titleAccent} onChange={(v) => updateField(key, 'titleAccent', v)} /></div>
                      </div>
                    </div>
                    <TextField label="Description" value={data.desc} onChange={(v) => updateField(key, 'desc', v)} multiline />
                    <div className="mb-3">
                      <label className="block text-white/50 text-xs mb-1">Statistiques</label>
                      {(data.stats || []).map((s, i) => (
                        <div key={i} className="flex gap-2 mb-1.5">
                          <input type="text" value={s.value} placeholder="Valeur" onChange={(e) => updateStat(i, 'value', e.target.value)}
                            className="w-24 bg-black border border-white/15 px-3 py-1.5 text-white text-sm focus:border-red-500 focus:outline-none rounded" />
                          <input type="text" value={s.label} placeholder="Label" onChange={(e) => updateStat(i, 'label', e.target.value)}
                            className="flex-1 bg-black border border-white/15 px-3 py-1.5 text-white text-sm focus:border-red-500 focus:outline-none rounded" />
                        </div>
                      ))}
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <TextField label="Texte du bouton" value={data.ctaText} onChange={(v) => updateField(key, 'ctaText', v)} />
                      <TextField label="Lien du bouton" value={data.ctaLink} onChange={(v) => updateField(key, 'ctaLink', v)} />
                    </div>
                  </>
                )}

                {type === 'cta' && (
                  <>
                    <div className="flex gap-2">
                      <div className="flex-1"><TextField label="Titre" value={data.title} onChange={(v) => updateField(key, 'title', v)} /></div>
                      <div className="flex-1"><TextField label="Titre (accent rouge)" value={data.titleAccent} onChange={(v) => updateField(key, 'titleAccent', v)} /></div>
                    </div>
                    <TextField label="Description" value={data.desc} onChange={(v) => updateField(key, 'desc', v)} multiline />
                  </>
                )}

                {type === 'hero' && (
                  <>
                    <SectionBgUpload sectionKey={key} />
                    <TextField label="Catégorie" value={data.category} onChange={(v) => updateField(key, 'category', v)} />
                    <div className="flex gap-2">
                      <div className="flex-1"><TextField label="Titre" value={data.title} onChange={(v) => updateField(key, 'title', v)} /></div>
                      <div className="flex-1"><TextField label="Titre (accent rouge)" value={data.titleAccent} onChange={(v) => updateField(key, 'titleAccent', v)} /></div>
                    </div>
                    <TextField label="Description" value={data.desc} onChange={(v) => updateField(key, 'desc', v)} multiline />
                  </>
                )}

                {type === 'heroNoImage' && (
                  <>
                    <TextField label="Catégorie" value={data.category} onChange={(v) => updateField(key, 'category', v)} />
                    <div className="flex gap-2">
                      <div className="flex-1"><TextField label="Titre" value={data.title} onChange={(v) => updateField(key, 'title', v)} /></div>
                      <div className="flex-1"><TextField label="Titre (accent rouge)" value={data.titleAccent} onChange={(v) => updateField(key, 'titleAccent', v)} /></div>
                    </div>
                    <TextField label="Description" value={data.desc} onChange={(v) => updateField(key, 'desc', v)} multiline />
                  </>
                )}
              </div>
            )}
          </div>
        );
      })}

      <button
        onClick={() => handleSave()}
        disabled={saving}
        data-testid="save-content-btn"
        className="flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white px-6 py-3 font-medium transition-colors rounded disabled:opacity-50 mt-6"
      >
        <Save size={18} /> {saving ? 'Enregistrement...' : 'Enregistrer'}
      </button>
    </div>
  );
};
